import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSignupService } from "../service/signupServices";
import { getAccessToken } from "../../utils/authTokenStore";
import bgImage from "../../../assets/bg.png";

function getPasswordStrength(password) {
  if (password.length < 6) return "Weak";
  if (password.match(/[A-Z]/) && password.match(/[0-9]/) && password.length >= 8) return "Strong";
  if (password.length >= 6) return "Medium";
  return "Weak";
}

export default function SignupPage() {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState(""); 
  const [passwordError, setPasswordError] = useState(""); 
  const [showSuccess, setShowSuccess] = useState(false); 
  const navigate = useNavigate();
  const { signup, isLoading } = useSignupService();
  const isLoggedIn = !!getAccessToken();

  const passwordStrength = getPasswordStrength(password);

  const handleSubmit = (e) => {
    e.preventDefault();
    setPasswordError("");
    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match");
      return;
    }
    signup(
      { username, email, password },  
      {
        onSuccess: () => {
          setShowSuccess(true);
          setTimeout(() => navigate("/login"), 2500); 
        },
      }
    );
  };

  if (isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cover bg-center" style={{ backgroundImage: `url(${bgImage})` }}>
        <div className="bg-black/70 backdrop-blur-md p-8 rounded-lg max-w-md w-full text-white shadow-lg flex flex-col items-center">
          <h2 className="text-2xl font-bold mb-4 text-green-400">You are already logged in!</h2>
          <p className="mb-4 text-gray-300">No need to sign up again. Go to your <Link to="/profile" className="text-green-400 underline">profile</Link>.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-cover bg-center" style={{ backgroundImage: `url(${bgImage})` }}>
      <div className="bg-black/70 backdrop-blur-md p-8 rounded-lg max-w-md w-full text-white shadow-lg">
        <h2 className="text-3xl font-bold mb-6 text-green-400 text-center">Create Your Account</h2>
        {/* Success Card */}
        {showSuccess && (
          <div className="mb-6 bg-green-600/90 text-white rounded-lg px-4 py-5 text-center shadow-lg border border-green-300 flex flex-col items-center animate-fade-in">
            <svg className="mb-2" width="40" height="40" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="12" fill="#22c55e" opacity="0.2"/>
              <path d="M8 12.5l2.5 2.5 5-5" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
            <h2 className="text-xl font-bold mb-1">Signup Successful!</h2>
            <p className="text-sm text-white/90 mb-1">Welcome to FitTrack! Redirecting to login...</p>
            <span className="text-xs text-white/70">You will be redirected shortly.</span>
          </div>
        )}
        <form className="space-y-7" onSubmit={handleSubmit}>
          {/* Username */}
          <div className="relative">
            <input
              type="text"
              id="username"
              value={username}
              onChange={e => setUsername(e.target.value)}
              className="peer w-full px-4 py-2 bg-transparent border-b-2 border-green-400 text-white focus:outline-none focus:border-green-300 transition placeholder-transparent"
              placeholder="Username"
              required
            />
            <label htmlFor="username" className="absolute left-4 top-2 text-gray-400 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-focus:-top-4 peer-focus:text-xs peer-focus:text-green-300">
              Username
            </label>
          </div>
          {/* Email */}
          <div className="relative">
            <input
              type="email"
              id="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="peer w-full px-4 py-2 bg-transparent border-b-2 border-green-400 text-white focus:outline-none focus:border-green-300 transition placeholder-transparent"
              placeholder="Email"
              required
            />
            <label htmlFor="email" className="absolute left-4 top-2 text-gray-400 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-focus:-top-4 peer-focus:text-xs peer-focus:text-green-300">
              Email
            </label>
          </div>
          {/* Password */}
          <div className="relative">
            <input
              type="password"
              id="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="peer w-full px-4 py-2 bg-transparent border-b-2 border-green-400 text-white focus:outline-none focus:border-green-300 transition placeholder-transparent"
              placeholder="Password"
              required
            />
            <label htmlFor="password" className="absolute left-4 top-2 text-gray-400 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-focus:-top-4 peer-focus:text-xs peer-focus:text-green-300">
              Password
            </label>
            {/* Password strength indicator */}
            {password && (
              <div className={`mt-1 text-xs font-semibold ${
                passwordStrength === "Strong"
                  ? "text-green-400"
                  : passwordStrength === "Medium"
                  ? "text-yellow-400"
                  : "text-red-400"
              }`}>
                Password strength: {passwordStrength}
              </div>
            )}
          </div>
          {/* Confirm Password */}
          <div className="relative">
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              className="peer w-full px-4 py-2 bg-transparent border-b-2 border-green-400 text-white focus:outline-none focus:border-green-300 transition placeholder-transparent"
              placeholder="Confirm Password"
              required
            />
            <label htmlFor="confirmPassword" className="absolute left-4 top-2 text-gray-400 text-sm transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-base peer-focus:-top-4 peer-focus:text-xs peer-focus:text-green-300">
              Confirm Password
            </label>
            {passwordError && (
              <div className="mt-1 text-xs font-semibold text-red-400">
                {passwordError}
              </div>
            )}
          </div>
          <button
            type="submit"
            className="w-full bg-green-500 hover:bg-green-400 text-black font-semibold py-2 rounded-full transition"
            disabled={isLoading}
          >
            {isLoading ? "Signing up..." : "Sign Up"}
          </button>
        </form>
        <p className="mt-4 text-sm text-gray-300 text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-green-400 hover:underline">
            Login
          </Link>
        </p>
      </div>
    </div>
  );
}
